import { forwardRef, useCallback, useEffect, useRef, useState, type KeyboardEvent, type TouchEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { HelpTopicScreenshot } from './help-topic-detail.types';

interface HelpScreenshotCarouselProps {
  slides: HelpTopicScreenshot[];
  index: number;
  onIndexChange: (index: number) => void;
}

const ring =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_45%,transparent)]';

/**
 * Carrousel de captures d’écran d’une fiche d’aide : flèches, points, clavier (sens de lecture respecté),
 * balayage tactile et agrandissement plein écran.
 */
export const HelpScreenshotCarousel = forwardRef<HTMLElement, HelpScreenshotCarouselProps>(
  function HelpScreenshotCarousel({ slides, index, onIndexChange }, ref) {
    const { t, i18n } = useTranslation();
    const [zoomed, setZoomed] = useState(false);
    const [failed, setFailed] = useState<Record<string, boolean>>({});
    const touchStartX = useRef<number | null>(null);
    const rtl = (i18n.dir?.(i18n.language) ?? 'ltr') === 'rtl';
    const count = slides.length;
    const current = slides[Math.min(Math.max(index, 0), count - 1)];

    const go = useCallback(
      (delta: number) => {
        if (count === 0) return;
        onIndexChange((index + delta + count) % count);
      },
      [count, index, onIndexChange],
    );

    // Précharge la capture suivante pour un passage sans attente.
    useEffect(() => {
      if (count < 2) return;
      const next = slides[(index + 1) % count];
      const img = new Image();
      img.src = next.src;
    }, [index, count, slides]);

    useEffect(() => {
      if (!zoomed) return;
      const onKey = (e: globalThis.KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          e.preventDefault();
          setZoomed(false);
        }
      };
      window.addEventListener('keydown', onKey, true);
      return () => window.removeEventListener('keydown', onKey, true);
    }, [zoomed]);

    if (!current) return null;

    const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(rtl ? -1 : 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(rtl ? 1 : -1);
      }
    };

    const onTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0]?.clientX ?? null;
    };
    const onTouchEnd = (e: TouchEvent) => {
      const start = touchStartX.current;
      touchStartX.current = null;
      const end = e.changedTouches[0]?.clientX;
      if (start == null || end == null || Math.abs(end - start) < 40) return;
      const towardsStart = end > start; // doigt vers la droite
      go(towardsStart !== rtl ? -1 : 1);
    };

    const PrevIcon = rtl ? ChevronRight : ChevronLeft;
    const NextIcon = rtl ? ChevronLeft : ChevronRight;
    const counter = t('help.carousel.counter', { current: index + 1, total: count, defaultValue: '{{current}} / {{total}}' });

    return (
      <section
        ref={ref}
        aria-roledescription={t('help.carousel.roledescription', 'carrousel')}
        aria-label={t('help.carousel.label', 'Captures d’écran')}
        className="scroll-mt-4"
        onKeyDown={onKeyDown}
        tabIndex={-1}
      >
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800/60">
          <div
            className="relative aspect-[16/10] w-full bg-[#EEF2F8] dark:bg-gray-900"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            aria-live="polite"
          >
            {failed[current.src] ? (
              <div className="flex h-full items-center justify-center p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                {t('help.carousel.unavailable', 'Capture indisponible pour le moment.')}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setZoomed(true)}
                className={cn('group block h-full w-full cursor-zoom-in', ring)}
                aria-label={t('help.carousel.zoom', 'Agrandir la capture')}
              >
                <img
                  key={current.src}
                  src={current.src}
                  alt={`${current.title}. ${current.caption}`}
                  className="h-full w-full object-contain"
                  decoding="async"
                  onError={() => setFailed((prev) => ({ ...prev, [current.src]: true }))}
                />
                <span className="pointer-events-none absolute end-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                  <Maximize2 className="h-3 w-3" aria-hidden />
                  {t('help.carousel.zoomShort', 'Agrandir')}
                </span>
              </button>
            )}

            {count > 1 ? (
              <>
                <button
                  type="button"
                  onClick={() => go(-1)}
                  className={cn(
                    'absolute start-2 top-1/2 flex h-8 w-8 sm:h-10 sm:w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-gray-800 shadow-md transition hover:bg-white dark:bg-gray-800/95 dark:text-white',
                    ring,
                  )}
                  aria-label={t('help.carousel.previous', 'Capture précédente')}
                >
                  <PrevIcon className="h-5 w-5" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => go(1)}
                  className={cn(
                    'absolute end-2 top-1/2 flex h-8 w-8 sm:h-10 sm:w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-gray-800 shadow-md transition hover:bg-white dark:bg-gray-800/95 dark:text-white',
                    ring,
                  )}
                  aria-label={t('help.carousel.next', 'Capture suivante')}
                >
                  <NextIcon className="h-5 w-5" aria-hidden />
                </button>
              </>
            ) : null}
          </div>

          <div className="space-y-3 border-t border-gray-100 px-4 py-3 dark:border-gray-700">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{current.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-gray-600 dark:text-gray-300">{current.caption}</p>
              </div>
              <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold tabular-nums text-gray-600 dark:bg-gray-700 dark:text-gray-200">
                {counter}
              </span>
            </div>
            {count > 1 ? (
              <div className="flex flex-wrap items-center justify-center gap-1.5" role="tablist" aria-label={t('help.carousel.label', 'Captures d’écran')}>
                {slides.map((slide, i) => (
                  <button
                    key={slide.src}
                    type="button"
                    role="tab"
                    aria-selected={i === index}
                    aria-label={`${i + 1}. ${slide.title}`}
                    title={slide.title}
                    onClick={() => onIndexChange(i)}
                    className={cn(
                      'h-2.5 rounded-full transition-all',
                      i === index
                        ? 'w-6 bg-[color:var(--inst-player-primary,#0C1C38)] dark:bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_55%,white)]'
                        : 'w-2.5 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600',
                      ring,
                    )}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>

        {zoomed && typeof document !== 'undefined' ? createPortal(
          <div
            className="fixed inset-0 z-[10100] flex flex-col bg-black/85 p-3 sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-label={current.title}
            onClick={() => setZoomed(false)}
          >
            <div className="mb-2 flex items-center justify-between gap-3 text-white">
              <p className="min-w-0 truncate text-sm font-semibold">
                {current.title} · {counter}
              </p>
              <button
                type="button"
                onClick={() => setZoomed(false)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 hover:bg-white/25"
                aria-label={t('btn.close', 'Fermer')}
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <img src={current.src} alt={current.caption} className="min-h-0 flex-1 object-contain" />
          </div>,
          document.body,
        ) : null}
      </section>
    );
  },
);
